import { Box, Button, Chip, Flex, Table, Text } from "@powerledger/ui-component-lib";
import { startCase } from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Column, Row } from "react-table";

import { HeaderWithToolTip } from "@/app/components";
import { ForwardTradeChangeRequestModalMode } from "@/app/components/forward-trade-change-request-modal";
import { RefreshIconButton } from "@/app/components/refresh-icon-button";
import { TableActionsButtons } from "@/app/components/table-actions-buttons";
import { TableValueWrapper } from "@/app/components/table-value-wrapper";
import { AppDateFormats, formatDate } from "@/app/lib/format-date";
import { getTableTranslation } from "@/app/lib/get-translations-for-components";
import {
  ChangeRequestAction,
  ChangeRequestStatus,
  ChangeRequestType,
  TradeContractChangeRequest,
} from "@/app/types/generated/graphql";

import { changeRequestHistoryChipVariant, RequestDateChangeTableViewProps } from "./request-date-change-table.types";

export const RequestDateChangeTableView: React.FC<RequestDateChangeTableViewProps> = ({
  loading,
  tableData,
  reloadChangeRequestHistory,
  showAddNewRequestButton = false,
  onClickAddNewRequest,
  allowAddingNewRequest,
  viewOnly = false,
  hideActionColumn,
  actions,
}) => {
  const { t } = useTranslation();

  const columns = useMemo<Column<TradeContractChangeRequest>[]>(() => {
    return [
      {
        Header: "Requested On",
        accessor: "requestedOn",
        disableSortBy: false,
        Cell: ({ value }: { value: string }) => (
          <Text>{formatDate(value, { formatStyle: AppDateFormats.AbbreviatedMonthFormatWithTime })}</Text>
        ),
      },
      {
        Header: t("Payment Date"),
        accessor: "paymentDate",
        disableSortBy: false,
        Cell: ({ value }: { value: string }) => (
          <TableValueWrapper
            value={formatDate(value, {
              formatStyle: AppDateFormats.AbbreviatedMonthFormat,
              noTZ: true,
              noOffset: true,
            })}
          />
        ),
      },
      {
        Header: (
          <HeaderWithToolTip
            titleText={t("Delivery Date (On or Before)")}
            tooltipText={t(
              "Delivery will execute as soon as the seller assigns the full contracted volume that meets all requirements, any time on or before this date.",
            )}
          />
        ),
        accessor: "deliveryDate",
        disableSortBy: false,
        Cell: ({ value }: { value: string }) => (
          <TableValueWrapper
            value={formatDate(value, {
              formatStyle: AppDateFormats.AbbreviatedMonthFormat,
              noTZ: true,
              noOffset: true,
            })}
          />
        ),
      },
      {
        Header: "Status",
        accessor: "status",
        disableSortBy: true,
        Cell: ({ value }: { value: ChangeRequestStatus }) => (
          <Chip
            sx={{
              variant: changeRequestHistoryChipVariant[value],
            }}
            label={t(startCase(value))}
          />
        ),
      },
      {
        Header: t("Action"),
        id: "action",
        Cell: ({ row }: { row: Row<TradeContractChangeRequest> }) => {
          const { type, status, paymentDate, deliveryDate } = row.original;
          const canModifyChangeRequest = status === ChangeRequestStatus.Open && type === ChangeRequestType.Outgoing;
          const canResponseChangeRequest = status === ChangeRequestStatus.Open && type === ChangeRequestType.Incoming;
          return (
            <>
              {canModifyChangeRequest && (
                <TableActionsButtons
                  actions={[
                    <Button
                      key={row.id + 1}
                      variant="pill.compactSecondary"
                      sx={{ py: 0, px: 3, height: "30px", boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px" }}
                      data-testid="withdraw-change-request"
                      onClick={() => actions.onChangeRequestAction(row.original, ChangeRequestAction.Withdraw)}
                    >
                      {t("Withdraw")}
                    </Button>,
                    <Button
                      key={row.id + 2}
                      variant="pill.compactSecondary"
                      sx={{ py: 0, px: 3, height: "30px", boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px" }}
                      data-testid="edit-change-request"
                      onClick={() =>
                        actions.onUpdateOrCounter({
                          paymentDate,
                          deliveryDate,
                          mode: ForwardTradeChangeRequestModalMode.Edit,
                        })
                      }
                    >
                      {t("Edit")}
                    </Button>,
                  ]}
                />
              )}
              {canResponseChangeRequest && (
                <TableActionsButtons
                  actions={[
                    <Button
                      key={row.id + 3}
                      variant="pill.compactSecondary"
                      sx={{ py: 0, px: 3, height: "30px", boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px" }}
                      data-testid="accept-change-request"
                      onClick={() => actions.onChangeRequestAction(row.original, ChangeRequestAction.Accept)}
                    >
                      {t("Accept")}
                    </Button>,
                    <Button
                      key={row.id + 4}
                      variant="pill.compactSecondary"
                      sx={{ py: 0, px: 3, height: "30px", boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px" }}
                      data-testid="reject-change-request"
                      onClick={() => actions.onChangeRequestAction(row.original, ChangeRequestAction.Reject)}
                    >
                      {t("Reject")}
                    </Button>,
                    <Button
                      key={row.id + 5}
                      variant="pill.compactSecondary"
                      sx={{ py: 0, px: 3, height: "30px", boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px" }}
                      data-testid="counter-change-request"
                      onClick={() =>
                        actions.onUpdateOrCounter({
                          paymentDate,
                          deliveryDate,
                          mode: ForwardTradeChangeRequestModalMode.Counter,
                        })
                      }
                    >
                      {t("Counter")}
                    </Button>,
                  ]}
                />
              )}
            </>
          );
        },
      },
    ];
  }, [t, actions]);

  // hide table in see details view if there is no data
  if (viewOnly && tableData.length === 0) return null;

  return (
    <Box>
      <Flex sx={{ justifyContent: viewOnly ? "flex-start" : "flex-end", gap: 2, mb: 2 }}>
        {viewOnly ? (
          <Text
            sx={{ fontSize: 3, fontFamily: "MintGroteskV08", fontWeight: 600, mb: 2 }}
            data-testid="change-request-histories"
          >
            {t("Change Request Histories")}
          </Text>
        ) : (
          <>
            {showAddNewRequestButton && allowAddingNewRequest && (
              <Button variant="primary" onClick={onClickAddNewRequest}>
                {t("Add New Request")}
              </Button>
            )}
            <RefreshIconButton loading={loading} fetchData={reloadChangeRequestHistory} />
          </>
        )}
      </Flex>
      <Table
        columns={columns}
        dataSource={tableData}
        translation={getTableTranslation(t)}
        pagination={false}
        loading={loading}
        testId="request-date-change-table"
        initialState={viewOnly || hideActionColumn ? { hiddenColumns: ["action"] } : {}}
      />
    </Box>
  );
};
